import type { Rect } from '../core/math';

/**
 * Uniform spatial hash grid. Items are inserted into every cell their AABB overlaps.
 * Queries dedupe with a per-item `stamp` so each item is visited at most once per query.
 */
export interface GridItem {
  stamp: number;
}

export class SpatialGrid<T extends GridItem> {
  private cells = new Map<number, T[]>();
  private activeKeys: number[] = [];
  private stampCounter = 1;

  constructor(readonly cellSize: number) {}

  static key(cx: number, cz: number): number {
    return ((cx & 0xffff) << 16) | (cz & 0xffff);
  }

  cellOf(v: number): number {
    return Math.floor(v / this.cellSize);
  }

  insert(item: T, r: Rect): void {
    const x0 = this.cellOf(r.minX);
    const x1 = this.cellOf(r.maxX);
    const z0 = this.cellOf(r.minZ);
    const z1 = this.cellOf(r.maxZ);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cz = z0; cz <= z1; cz++) {
        const k = SpatialGrid.key(cx, cz);
        let list = this.cells.get(k);
        if (!list) {
          list = [];
          this.cells.set(k, list);
        }
        if (list.length === 0) this.activeKeys.push(k);
        list.push(item);
      }
    }
  }

  remove(item: T, r: Rect): void {
    const x0 = this.cellOf(r.minX);
    const x1 = this.cellOf(r.maxX);
    const z0 = this.cellOf(r.minZ);
    const z1 = this.cellOf(r.maxZ);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cz = z0; cz <= z1; cz++) {
        const list = this.cells.get(SpatialGrid.key(cx, cz));
        if (!list) continue;
        const i = list.indexOf(item);
        if (i >= 0) list.splice(i, 1);
      }
    }
  }

  /** Empty all cells (keeps arrays allocated). */
  clear(): void {
    for (const k of this.activeKeys) {
      const list = this.cells.get(k);
      if (list) list.length = 0;
    }
    this.activeKeys.length = 0;
  }

  /** Start a new dedupe pass; returns the stamp to compare against. */
  nextStamp(): number {
    return ++this.stampCounter;
  }

  /** Visit each distinct item overlapping the rect's cells (broad test only). */
  query(r: Rect, visit: (item: T) => void): void {
    const stamp = this.nextStamp();
    const x0 = this.cellOf(r.minX);
    const x1 = this.cellOf(r.maxX);
    const z0 = this.cellOf(r.minZ);
    const z1 = this.cellOf(r.maxZ);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cz = z0; cz <= z1; cz++) {
        const list = this.cells.get(SpatialGrid.key(cx, cz));
        if (!list) continue;
        for (let i = 0; i < list.length; i++) {
          const item = list[i] as T;
          if (item.stamp === stamp) continue;
          item.stamp = stamp;
          visit(item);
        }
      }
    }
  }

  /** Items in a single cell (may contain duplicates across cells). */
  cell(cx: number, cz: number): readonly T[] | undefined {
    return this.cells.get(SpatialGrid.key(cx, cz));
  }
}
